import type BackOfficeConnector from "../data/backOfficeConnector";
import type { IPagination } from "../data/types";
import BlacklistedEmailAddress from "../entities/blacklistedEmailAddress";
import type {
  BlacklistedEmailAddressCreateRequest,
  BlacklistedEmailAddressUpdateRequest,
} from "../entities/blacklistedEmailAddressRequest";
import type EmailBlacklistFilter from "../entities/filters/emailBlacklistFilter";
import type EventBus from "../services/eventBus";
import { serializeFilters } from "../utils/filters";
import RepositoryBase from "./repositoryBase";
import type { ICreateRepository } from "./types";

export default class EmailBlacklistRepository
  extends RepositoryBase<BackOfficeConnector>
  implements ICreateRepository<BlacklistedEmailAddressCreateRequest>
{
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  getAll(pagination: IPagination, filter?: EmailBlacklistFilter) {
    const requestBody = {
      ...serializeFilters(filter, false),
      offset: pagination.offset ?? 0,
      limit: pagination.limit ?? 100,
    };

    return this.callApi(
      api =>
        api
          .all("charges")
          .all("blacklisted_email_address_search")
          .postPagedEntities<BlacklistedEmailAddress>(requestBody, BlacklistedEmailAddress),
      { ownApiFactory: () => this.apiFactory({ customVersion: "2" }) }
    );
  }

  get(id: string) {
    return this.callApi(
      api =>
        api.all("charges").one("blacklisted_email_addresses", id).getEntity<BlacklistedEmailAddress>(BlacklistedEmailAddress),
      { ownApiFactory: () => this.apiFactory({ customVersion: "2" }) }
    );
  }

  create(request: BlacklistedEmailAddressCreateRequest) {
    return this.callApi(
      api =>
        api
          .all("charges")
          .all("blacklisted_email_addresses")
          .postEntity<BlacklistedEmailAddress>(serializeFilters(request, false), BlacklistedEmailAddress),
      { ownApiFactory: () => this.apiFactory({ customVersion: "2" }) }
    );
  }

  update(id: string, request: BlacklistedEmailAddressUpdateRequest) {
    return this.callApi(
      api => api.all("charges").one("blacklisted_email_addresses", id).putOnly(serializeFilters(request, false)),
      { ownApiFactory: () => this.apiFactory({ customVersion: "2" }) }
    );
  }

  deactivate(id: string) {
    const request: BlacklistedEmailAddressUpdateRequest = {
      hasImmediateEndOfValidity: true,
      validUntilActionType: "CHANGE",
    };
    return this.update(id, request);
  }
}
